import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { primeBlotatoReferral } from "@/lib/blotato";
import { captureAttribution } from "@/lib/attribution";
import AppLayout from "@/components/AppLayout";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import LandingPublic from "@/pages/LandingPublic";
import PrivacyPage from "@/pages/legal/PrivacyPage";
import TermsPage from "@/pages/legal/TermsPage";
import CompliancePage from "@/pages/legal/CompliancePage";
import SenderAgreementPage from "@/pages/legal/SenderAgreementPage";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import Opportunities from "@/pages/Opportunities";
import OpportunityDetail from "@/pages/OpportunityDetail";
import Pipeline from "@/pages/Pipeline";
import Assistant from "@/pages/Assistant";
import Reports from "@/pages/Reports";
import Alerts from "@/pages/Alerts";
import Outreach from "@/pages/Outreach";
import Leads from "@/pages/Leads";
import SellerFunnel from "@/pages/SellerFunnel";
// BLOCK 19 — SellerForm retired in favor of /submit-{type}; kept file on disk
// for reference/history but no longer imported or routed.
// import SellerForm from "@/pages/SellerForm";
import Billing from "@/pages/Billing";
import BillingSuccess from "@/pages/BillingSuccess";
import Settings from "@/pages/Settings";
import UserManagement from "@/pages/UserManagement";
import Tasks from "@/pages/Tasks";
import BulkImport from "@/pages/BulkImport";
import Referrals from "@/pages/Referrals";
import AdminReferrals from "@/pages/AdminReferrals";
import AuditLog from "@/pages/AuditLog";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import SellerClaim from "@/pages/SellerClaim";
import SellerDashboard from "@/pages/SellerDashboard";
import ProspectIntake from "@/pages/ProspectIntake";
import DataSources from "@/pages/DataSources";
import SubmissionsInbox from "@/pages/SubmissionsInbox";
import SubmitBusiness from "@/pages/submit/SubmitBusiness";
import SubmitLand from "@/pages/submit/SubmitLand";
import SubmitMultifamily from "@/pages/submit/SubmitMultifamily";
import PipelineV2 from "@/pages/PipelineV2";
import DiscoveryAdmin from "@/pages/DiscoveryAdmin";
import Pricing from "@/pages/Pricing";
import DocumentLibrary from "@/pages/DocumentLibrary";
import DiscoveryConfigsAdmin from "@/pages/DiscoveryConfigsAdmin";
import SeoAdmin from "@/pages/SeoAdmin";
import AdminPlatformOps from "@/pages/AdminPlatformOps";
import AdminLegalPagesPage from "@/pages/AdminLegalPagesPage";
import AdminLegalSourcing from "@/pages/AdminLegalSourcing";
import AdminAttribution from "@/pages/AdminAttribution";
import Help from "@/pages/Help";

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#05070A] flex items-center justify-center text-slate-500">
        Loading…
      </div>
    );
  }
  // BLOCK 40 — preserve the intended destination so Login.jsx can bounce
  // the user back after a successful login (e.g. digest "View →" click
  // while logged out lands on the opportunity, not the dashboard).
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return children;
}

// BLOCK 70 Part B — /dashboard route gate.
// Admin / super_admin / manager see the platform-wide AdminDashboard;
// every other role continues to see the subscriber Dashboard, unchanged.
// Kept as a conditional render (not a separate route) so all existing
// bookmarks, post-login redirects, and sidebar links keep working.
function DashboardGate() {
  const { user } = useAuth();
  const role = user?.role;
  if (role === "admin" || role === "super_admin" || role === "manager") {
    return <AdminDashboard />;
  }
  return <Dashboard />;
}

// BLOCK 167 — seller accounts are a separate, minimal surface: they never
// see AppLayout's subscriber/admin nav (which points at endpoints they're
// locked out of). A non-seller landing here bounces to the normal dashboard.
function RequireSeller({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#05070A] flex items-center justify-center text-slate-500">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "seller") return <Navigate to="/app/dashboard" replace />;
  return children;
}

export default function App() {
  // Prime the Blotato referral param once on app boot so buildBlotatoLink()
  // returns a fully-qualified URL synchronously in render.
  useEffect(() => { primeBlotatoReferral(); }, []);
  // BLOCK 187 — capture first-touch signup attribution (utm params +
  // referrer) once on app boot. First touch wins; no-op on repeat visits.
  useEffect(() => { captureAttribution(); }, []);
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster theme="dark" position="top-right" richColors />
          <Routes>
          <Route path="/" element={<LandingPublic />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot" element={<ForgotPassword />} />
          <Route path="/reset" element={<ResetPassword />} />
          <Route path="/seller/claim" element={<SellerClaim />} />
          <Route
            path="/seller/dashboard"
            element={
              <RequireSeller>
                <SellerDashboard />
              </RequireSeller>
            }
          />
          <Route path="/sell" element={<SellerFunnel />} />
          {/* BLOCK 19 — /sell/:type retired; redirect any inbound to the merged funnel */}
          <Route path="/sell/:type" element={<Navigate to="/sell" replace />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/signup" element={<Register />} />
          <Route path="/submit-business" element={<SubmitBusiness />} />
          <Route path="/submit-land" element={<SubmitLand />} />
          <Route path="/submit-multifamily" element={<SubmitMultifamily />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/compliance" element={<CompliancePage />} />
          <Route path="/sender-agreement" element={<SenderAgreementPage />} />

          <Route
            path="/app"
            element={
              <RequireAuth>
                <AppLayout />
              </RequireAuth>
            }
          >
            <Route index element={<Navigate to="dashboard" replace />} />
            {/* BLOCK 184 Part B.2 — every admin-only page is wrapped in its
                own ErrorBoundary so a render crash in ONE page degrades to
                a contained "Retry" card instead of white-screening the
                whole app. Non-admin pages are intentionally left
                unwrapped — this scoping matches the explicit requirement
                (admin pages) and keeps the blast radius per-page. */}
            <Route path="dashboard" element={<ErrorBoundary section="Dashboard"><DashboardGate /></ErrorBoundary>} />
            <Route path="opportunities" element={<Opportunities />} />
            <Route path="opportunities/:oid" element={<OpportunityDetail />} />
            <Route path="intake" element={<ProspectIntake />} />
            <Route path="pipeline" element={<PipelineV2 />} />
            <Route path="pipeline-v2" element={<Navigate to="../pipeline" replace />} />
            <Route path="assistant" element={<Assistant />} />
            <Route path="leads" element={<Leads />} />
            <Route path="tasks" element={<Tasks />} />
            <Route path="outreach" element={<Outreach />} />
            <Route path="alerts" element={<Alerts />} />
            <Route path="reports" element={<Reports />} />
            <Route path="import" element={<BulkImport />} />
            <Route path="billing" element={<Billing />} />
            <Route path="billing/success" element={<BillingSuccess />} />
            <Route path="users" element={<ErrorBoundary section="Users"><UserManagement /></ErrorBoundary>} />
            <Route path="settings" element={<Settings />} />
            <Route path="help" element={<Help />} />
            <Route path="documents/library" element={<DocumentLibrary />} />
            <Route path="admin/discovery/configs" element={<ErrorBoundary section="County Connectors"><DiscoveryConfigsAdmin /></ErrorBoundary>} />
            <Route path="admin/seo" element={<ErrorBoundary section="SEO"><SeoAdmin /></ErrorBoundary>} />
            <Route path="admin/legal-pages" element={<ErrorBoundary section="Legal Pages"><AdminLegalPagesPage /></ErrorBoundary>} />
            <Route path="admin/legal-sourcing" element={<ErrorBoundary section="Legal Sourcing"><AdminLegalSourcing /></ErrorBoundary>} />
            <Route path="referrals" element={<Referrals />} />
            <Route path="admin/referrals" element={<ErrorBoundary section="Payouts"><AdminReferrals /></ErrorBoundary>} />
            <Route path="admin/audit" element={<ErrorBoundary section="Audit Log"><AuditLog /></ErrorBoundary>} />
            <Route path="admin/data-sources" element={<ErrorBoundary section="Data Sources"><DataSources /></ErrorBoundary>} />
            <Route path="admin/submissions" element={<ErrorBoundary section="Pending Review"><SubmissionsInbox /></ErrorBoundary>} />
            <Route path="admin/discovery" element={<ErrorBoundary section="Lead Discovery"><DiscoveryAdmin /></ErrorBoundary>} />
            <Route path="admin/platform-ops" element={<ErrorBoundary section="Platform Ops"><AdminPlatformOps /></ErrorBoundary>} />
            <Route path="admin/attribution" element={<ErrorBoundary section="Signup Attribution"><AdminAttribution /></ErrorBoundary>} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </HelmetProvider>
  );
}
